<script setup lang="ts">
import { nextTick, onBeforeUnmount, onMounted, ref } from 'vue'

import { api, apiSse, messageFrom, type SseMessage } from '../api'
import type {
  PublicConversationMessage,
  PublicLibrarySource,
  PublicSource,
  PublicSpeaker,
} from '../types'
import { getTelegramMiniApp, isTelegramMiniAppPath } from '../telegram'

const props = defineProps<{ slug: string }>()
const emit = defineEmits<{ navigate: [path: string] }>()
const speaker = ref<PublicSpeaker | null>(null)
const messages = ref<PublicConversationMessage[]>([])
const question = ref('')
const loading = ref(true)
const sending = ref(false)
const error = ref('')
type VoicePhase =
  | 'idle'
  | 'requesting'
  | 'recording'
  | 'uploading'
  | 'transcribing'
  | 'answering'
  | 'preparingVoice'
const voicePhase = ref<VoicePhase>('idle')
const recordingSeconds = ref(0)
const voiceInputAvailable = ref(false)
const voicePollTimers = new Map<string, number>()
const voicePolling = new Set<string>()
const voiceProgressTimers = new Set<number>()
const streamingVoicePlayer = ref<HTMLAudioElement | null>(null)
let mediaRecorder: MediaRecorder | null = null
let microphoneStream: MediaStream | null = null
let recordingTimer: number | null = null
let discardRecording = false
let activeVoiceAnswerId: string | null = null
let streamingVoiceAnswerId: string | null = null
let streamingVoiceMessage: PublicConversationMessage | null = null
let streamingVoicePartUrls: string[] = []
let streamingVoicePartIndex = 0
let streamingVoiceTotalParts: number | null = null
let streamingVoicePlaying = false
let streamingVoiceStarting = false
let streamingVoiceGeneration = 0
let pageUnmounted = false
let activeQuestionRequest: AbortController | null = null
let streamScrollScheduled = false

const MAX_VOICE_RECORDING_SECONDS = 90
const RECORDING_MIME_TYPES = [
  'audio/webm;codecs=opus',
  'audio/webm',
  'audio/mp4',
] as const

const storageKey = `spikerskie:conversation:v1:${props.slug}`

function validStoredMessage(value: unknown): value is PublicConversationMessage {
  if (!value || typeof value !== 'object') return false
  const message = value as Partial<PublicConversationMessage>
  return (message.role === 'user' || message.role === 'speaker')
    && typeof message.text === 'string'
    && message.text.length > 0
    && (message.sources === undefined || Array.isArray(message.sources))
    && (message.librarySources === undefined || Array.isArray(message.librarySources))
    && (message.answerId === undefined || typeof message.answerId === 'string')
    && (message.voiceStatus === undefined || ['idle', 'pending', 'ready', 'failed'].includes(message.voiceStatus))
    && (message.voiceAutoplay === undefined || typeof message.voiceAutoplay === 'boolean')
}

function restoreConversation(): void {
  try {
    const parsed = JSON.parse(sessionStorage.getItem(storageKey) || '[]') as unknown
    if (Array.isArray(parsed)) {
      messages.value = parsed
        .filter(validStoredMessage)
        .slice(-40)
        .map(message => ({ ...message, voiceAutoplay: false }))
    }
  } catch {
    messages.value = []
  }
}

function saveConversation(): void {
  try { sessionStorage.setItem(storageKey, JSON.stringify(messages.value.slice(-40))) } catch { /* диалог останется до перезагрузки */ }
}

function timecode(seconds: number): string {
  const value = Math.max(0, Math.floor(seconds))
  const hours = Math.floor(value / 3600)
  const minutes = Math.floor((value % 3600) / 60)
  const secs = String(value % 60).padStart(2, '0')
  return hours
    ? `${hours}:${String(minutes).padStart(2, '0')}:${secs}`
    : `${minutes}:${secs}`
}

function sourceAudio(source: PublicSource): string {
  return `/api/public/fragment/${encodeURIComponent(source.chunkId)}/audio`
}

function parseStreamData<T>(message: SseMessage): T {
  try {
    return JSON.parse(message.data) as T
  } catch {
    throw new Error('Сервер прислал повреждённый поток ответа.')
  }
}

function nearPageBottom(): boolean {
  return window.scrollY + window.innerHeight >= document.body.scrollHeight - 180
}

function scheduleStreamScroll(): void {
  if (streamScrollScheduled || !nearPageBottom()) return
  streamScrollScheduled = true
  void nextTick().then(() => {
    window.requestAnimationFrame(() => {
      streamScrollScheduled = false
      window.scrollTo({ top: document.body.scrollHeight, behavior: 'auto' })
    })
  })
}

function isAbortError(reason: unknown): boolean {
  return reason instanceof Error && reason.name === 'AbortError'
}

function removeEmptyMessage(message: PublicConversationMessage | null): void {
  if (!message?.text) {
    const index = message ? messages.value.indexOf(message) : -1
    if (index >= 0) messages.value.splice(index, 1)
  }
}

async function load(): Promise<void> {
  loading.value = true
  error.value = ''
  try {
    speaker.value = (await api<{ speaker: PublicSpeaker }>(
      `/api/public/speakers/${encodeURIComponent(props.slug)}`,
    )).speaker
    for (const message of messages.value) {
      if (message.role === 'speaker' && message.answerId && message.voiceStatus === 'pending') scheduleVoicePoll(message)
    }
  } catch (reason) {
    error.value = messageFrom(reason)
  } finally {
    loading.value = false
  }
}

async function ask(): Promise<void> {
  const text = question.value.trim()
  if (!text || sending.value || voicePhase.value !== 'idle' || !speaker.value) return
  const history = messages.value.slice(-4).map(message => ({ role: message.role, text: message.text }))
  question.value = ''
  error.value = ''
  messages.value.push({ role: 'user', text })
  saveConversation()
  sending.value = true
  const controller = new AbortController()
  activeQuestionRequest = controller
  await nextTick()
  window.scrollTo({ top: document.body.scrollHeight, behavior: 'smooth' })
  let answerMessage: PublicConversationMessage | null = null
  let finalReceived = false
  const ensureAnswerMessage = (): PublicConversationMessage => {
    if (answerMessage) return answerMessage
    messages.value.push({
      role: 'speaker',
      text: '',
    })
    answerMessage = messages.value[messages.value.length - 1]!
    return answerMessage
  }
  try {
    await apiSse(
      `/api/public/speakers/${encodeURIComponent(props.slug)}/questions/stream`,
      {
        method: 'POST',
        body: JSON.stringify({ question: text, history }),
        signal: controller.signal,
      },
      message => {
        if (message.event === 'delta') {
          const payload = parseStreamData<{ text?: unknown }>(message)
          if (typeof payload.text !== 'string') throw new Error('Сервер прислал повреждённый поток ответа.')
          if (!payload.text) return
          ensureAnswerMessage().text += payload.text
          scheduleStreamScroll()
        } else if (message.event === 'final') {
          const response = parseStreamData<{
            id: string
            answer: string
            sources: PublicSource[]
            librarySources: PublicLibrarySource[]
          }>(message)
          const answer = ensureAnswerMessage()
          answer.text = response.answer
          answer.sources = response.sources
          answer.librarySources = response.librarySources
          answer.answerId = response.id
          answer.voiceStatus = 'idle'
          finalReceived = true
          saveConversation()
        } else if (message.event === 'error') {
          const payload = parseStreamData<{ error?: unknown }>(message)
          throw new Error(typeof payload.error === 'string'
            ? payload.error
            : 'Поток ответа оборвался. Попробуй ещё раз.')
        }
      },
    )
    if (!finalReceived) throw new Error('Поток ответа оборвался. Попробуй ещё раз.')
  } catch (reason) {
    if (isAbortError(reason)) return
    removeEmptyMessage(answerMessage)
    error.value = messageFrom(reason)
    question.value = text
    saveConversation()
  } finally {
    if (activeQuestionRequest === controller) activeQuestionRequest = null
    sending.value = false
    await nextTick()
    if (nearPageBottom()) window.scrollTo({ top: document.body.scrollHeight, behavior: 'auto' })
  }
}

interface VoiceResponse {
  status: 'pending' | 'ready' | 'failed'
  audioUrl: string | null
  readyParts: number
  totalParts: number | null
  partUrls: string[]
}

function stopVoicePoll(answerId: string): void {
  const timer = voicePollTimers.get(answerId)
  if (timer !== undefined) window.clearInterval(timer)
  voicePollTimers.delete(answerId)
  voicePolling.delete(answerId)
}

function stopStreamingVoice(): void {
  streamingVoiceGeneration += 1
  streamingVoicePlayer.value?.pause()
  streamingVoicePlayer.value?.removeAttribute('src')
  streamingVoicePlayer.value?.load()
  streamingVoiceAnswerId = null
  streamingVoiceMessage = null
  streamingVoicePartUrls = []
  streamingVoicePartIndex = 0
  streamingVoiceTotalParts = null
  streamingVoicePlaying = false
  streamingVoiceStarting = false
}

function pauseFullVoicePlayers(exceptAnswerId?: string): void {
  const exceptId = exceptAnswerId ? `answer-voice-${exceptAnswerId}` : null
  for (const player of document.querySelectorAll<HTMLAudioElement>('audio[id^="answer-voice-"]')) {
    if (player.id !== exceptId) player.pause()
  }
}

async function playNextStreamingPart(): Promise<void> {
  if (!streamingVoiceAnswerId || streamingVoicePlaying || streamingVoiceStarting) return
  if (streamingVoiceTotalParts !== null && streamingVoicePartIndex >= streamingVoiceTotalParts) {
    stopStreamingVoice()
    return
  }
  const partUrl = streamingVoicePartUrls[streamingVoicePartIndex]
  if (!partUrl) return

  streamingVoiceStarting = true
  const answerId = streamingVoiceAnswerId
  const generation = streamingVoiceGeneration
  await nextTick()
  const player = streamingVoicePlayer.value
  if (!player || generation !== streamingVoiceGeneration || answerId !== streamingVoiceAnswerId) {
    if (generation === streamingVoiceGeneration) streamingVoiceStarting = false
    return
  }

  player.src = partUrl
  player.load()
  try {
    await player.play()
    if (generation !== streamingVoiceGeneration || answerId !== streamingVoiceAnswerId) {
      player.pause()
      return
    }
    streamingVoiceStarting = false
    streamingVoicePlaying = true
    if (activeVoiceAnswerId === answerId) {
      activeVoiceAnswerId = null
      voicePhase.value = 'idle'
    }
    saveConversation()
  } catch {
    if (generation !== streamingVoiceGeneration || answerId !== streamingVoiceAnswerId) return
    const message = streamingVoiceMessage
    stopStreamingVoice()
    if (message) message.voiceAutoplay = false
    if (activeVoiceAnswerId === answerId) {
      activeVoiceAnswerId = null
      voicePhase.value = 'idle'
    }
    error.value = 'Браузер запретил автоматическое воспроизведение. Дождись готовности и нажми Play.'
    saveConversation()
  }
}

function startStreamingVoice(message: PublicConversationMessage, response: VoiceResponse): void {
  if (!message.answerId || !response.partUrls.length) return
  stopStreamingVoice()
  pauseFullVoicePlayers()
  streamingVoiceAnswerId = message.answerId
  streamingVoiceMessage = message
  streamingVoicePartUrls = [...response.partUrls]
  streamingVoiceTotalParts = response.totalParts
  message.voiceAutoplay = false
  void playNextStreamingPart()
}

function handleFullVoicePlay(answerId: string): void {
  if (streamingVoiceAnswerId) stopStreamingVoice()
  pauseFullVoicePlayers(answerId)
}

function updateStreamingVoice(message: PublicConversationMessage, response: VoiceResponse): void {
  if (!message.answerId) return
  if (streamingVoiceAnswerId === message.answerId) {
    streamingVoicePartUrls = [...response.partUrls]
    streamingVoiceTotalParts = response.totalParts
    if (response.status === 'failed') {
      stopStreamingVoice()
      return
    }
    void playNextStreamingPart()
    return
  }
  if (message.voiceAutoplay && response.status !== 'failed' && response.partUrls.length) {
    startStreamingVoice(message, response)
  }
}

function handleStreamingPartEnded(): void {
  if (!streamingVoiceAnswerId) return
  streamingVoicePlaying = false
  streamingVoicePartIndex += 1
  if (streamingVoiceTotalParts !== null && streamingVoicePartIndex >= streamingVoiceTotalParts) {
    stopStreamingVoice()
    return
  }
  void playNextStreamingPart()
}

function applyVoiceStatus(message: PublicConversationMessage, response: VoiceResponse): void {
  message.voiceStatus = response.status
  message.voiceUrl = response.audioUrl ?? undefined
  if (response.status !== 'pending' && message.answerId) stopVoicePoll(message.answerId)
  updateStreamingVoice(message, response)
  if (response.status === 'ready' && message.voiceAutoplay && response.audioUrl
    && !response.partUrls.length) {
    void playVoiceAutomatically(message)
  }
  if (message.answerId === activeVoiceAnswerId && response.status === 'failed') {
    activeVoiceAnswerId = null
    voicePhase.value = 'idle'
    error.value = 'Не удалось подготовить голос спикера.'
  } else if (message.answerId === activeVoiceAnswerId && response.status === 'ready'
    && !response.partUrls.length) {
    activeVoiceAnswerId = null
    voicePhase.value = 'idle'
  }
  saveConversation()
}

async function pollVoice(message: PublicConversationMessage): Promise<void> {
  const answerId = message.answerId
  if (!answerId || voicePolling.has(answerId)) return
  voicePolling.add(answerId)
  try {
    const response = await api<VoiceResponse>(
      `/api/public/answers/${encodeURIComponent(answerId)}/voice`,
    )
    applyVoiceStatus(message, response)
  } catch {
    // Временная ошибка опроса не должна сбрасывать уже поставленную фоновую задачу.
  } finally {
    voicePolling.delete(answerId)
  }
}

function scheduleVoicePoll(message: PublicConversationMessage): void {
  if (!message.answerId || voicePollTimers.has(message.answerId)) return
  const timer = window.setInterval(() => { void pollVoice(message) }, 500)
  voicePollTimers.set(message.answerId, timer)
}

async function requestVoice(message: PublicConversationMessage): Promise<void> {
  if (!message.answerId || message.voiceStatus === 'pending') return
  message.voiceStatus = 'pending'
  saveConversation()
  try {
    const response = await api<VoiceResponse>(
      `/api/public/answers/${encodeURIComponent(message.answerId)}/voice`,
      { method: 'POST', body: '{}' },
    )
    applyVoiceStatus(message, response)
    if (response.status === 'pending') scheduleVoicePoll(message)
  } catch (reason) {
    message.voiceStatus = 'failed'
    error.value = messageFrom(reason)
    saveConversation()
  }
}

function supportsVoiceInput(): boolean {
  if (!isTelegramMiniAppPath() || !getTelegramMiniApp()?.initData) return false
  if (typeof MediaRecorder === 'undefined'
    || typeof MediaRecorder.isTypeSupported !== 'function'
    || !navigator.mediaDevices?.getUserMedia) return false
  return RECORDING_MIME_TYPES.some(type => MediaRecorder.isTypeSupported(type))
}

function voiceButtonLabel(): string {
  if (voicePhase.value === 'requesting') return 'Открываю микрофон…'
  if (voicePhase.value === 'recording') return `Говорите · ${timecode(recordingSeconds.value)}`
  if (voicePhase.value === 'uploading') return 'Отправляю…'
  if (voicePhase.value === 'transcribing') return 'Расшифровываю…'
  if (voicePhase.value === 'answering') return 'Спикер отвечает…'
  if (voicePhase.value === 'preparingVoice') return 'Готовлю голос…'
  return 'Говорить'
}

function clearVoiceProgressTimers(): void {
  for (const timer of voiceProgressTimers) window.clearTimeout(timer)
  voiceProgressTimers.clear()
}

function scheduleVoiceProgress(callback: () => void, delay: number): void {
  const timer = window.setTimeout(() => {
    voiceProgressTimers.delete(timer)
    callback()
  }, delay)
  voiceProgressTimers.add(timer)
}

function clearRecordingTimer(): void {
  if (recordingTimer !== null) window.clearInterval(recordingTimer)
  recordingTimer = null
}

function closeMicrophone(): void {
  microphoneStream?.getTracks().forEach(track => track.stop())
  microphoneStream = null
}

// Поток микрофона живёт между записями: остановка дорожек заставляет Telegram
// спрашивать разрешение заново перед каждой репликой, а разговор — это
// десяток реплик подряд. Дорожки закрываются при уходе со страницы.
async function acquireMicrophone(): Promise<MediaStream> {
  const live = microphoneStream?.getAudioTracks().some(track => track.readyState === 'live')
  if (microphoneStream && live) return microphoneStream
  closeMicrophone()
  const stream = await navigator.mediaDevices.getUserMedia({ audio: true })
  microphoneStream = stream
  return stream
}

function stopVoiceRecording(send = true): void {
  if (!mediaRecorder || voicePhase.value !== 'recording') return
  discardRecording = !send
  clearRecordingTimer()
  try {
    mediaRecorder.stop()
  } catch {
    // Только здесь дорожки закрываем принудительно: рекордер сломан, поток
    // переиспользовать нельзя. В обычном завершении записи микрофон остаётся
    // живым, иначе Telegram спрашивает разрешение перед каждой репликой.
    closeMicrophone()
    mediaRecorder = null
    voicePhase.value = 'idle'
    if (send) error.value = 'Не удалось завершить запись с микрофона.'
  }
}

function recordingFilename(mimeType: string): string {
  return mimeType.includes('mp4') ? 'question.m4a' : 'question.webm'
}

async function sendVoiceQuestion(audio: Blob): Promise<void> {
  if (!speaker.value) {
    voicePhase.value = 'idle'
    return
  }
  if (!audio.size) {
    error.value = 'Микрофон не записал звук. Попробуй ещё раз.'
    voicePhase.value = 'idle'
    return
  }

  const history = messages.value.slice(-4).map(message => ({ role: message.role, text: message.text }))
  const form = new FormData()
  form.append('history', JSON.stringify(history))
  form.append('audio', audio, recordingFilename(audio.type))
  error.value = ''
  voicePhase.value = 'uploading'
  scheduleVoiceProgress(() => {
    if (voicePhase.value === 'uploading') voicePhase.value = 'transcribing'
  }, 500)
  scheduleVoiceProgress(() => {
    if (voicePhase.value === 'uploading' || voicePhase.value === 'transcribing') {
      voicePhase.value = 'answering'
    }
  }, 3_500)

  const controller = new AbortController()
  activeQuestionRequest = controller
  let recognizedQuestion = ''
  let answerMessage: PublicConversationMessage | null = null
  let finalReceived = false
  const ensureAnswerMessage = (): PublicConversationMessage => {
    if (answerMessage) return answerMessage
    messages.value.push({ role: 'speaker', text: '' })
    answerMessage = messages.value[messages.value.length - 1]!
    return answerMessage
  }
  try {
    await apiSse(
      `/api/public/speakers/${encodeURIComponent(props.slug)}/voice-questions/stream`,
      { method: 'POST', body: form, signal: controller.signal },
      message => {
        if (message.event === 'question') {
          const payload = parseStreamData<{ question?: unknown }>(message)
          if (typeof payload.question !== 'string' || !payload.question) {
            throw new Error('Сервер не вернул распознанный вопрос.')
          }
          clearVoiceProgressTimers()
          recognizedQuestion = payload.question
          messages.value.push({ role: 'user', text: recognizedQuestion })
          voicePhase.value = 'answering'
          saveConversation()
          scheduleStreamScroll()
        } else if (message.event === 'delta') {
          const payload = parseStreamData<{ text?: unknown }>(message)
          if (typeof payload.text !== 'string') throw new Error('Сервер прислал повреждённый поток ответа.')
          if (!payload.text) return
          ensureAnswerMessage().text += payload.text
          voicePhase.value = 'answering'
          scheduleStreamScroll()
        } else if (message.event === 'final') {
          const response = parseStreamData<{
            id: string
            answer: string
            sources: PublicSource[]
            librarySources: PublicLibrarySource[]
          }>(message)
          const answer = ensureAnswerMessage()
          answer.text = response.answer
          answer.sources = response.sources
          answer.librarySources = response.librarySources
          answer.answerId = response.id
          answer.voiceStatus = 'pending'
          answer.voiceAutoplay = true
          activeVoiceAnswerId = response.id
          voicePhase.value = 'preparingVoice'
          finalReceived = true
          saveConversation()
          scheduleVoicePoll(answer)
          void pollVoice(answer)
        } else if (message.event === 'error') {
          const payload = parseStreamData<{ error?: unknown }>(message)
          throw new Error(typeof payload.error === 'string'
            ? payload.error
            : 'Поток голосового ответа оборвался. Попробуй ещё раз.')
        }
      },
    )
    if (!recognizedQuestion || !finalReceived) {
      throw new Error('Поток голосового ответа оборвался. Попробуй ещё раз.')
    }
    clearVoiceProgressTimers()
  } catch (reason) {
    clearVoiceProgressTimers()
    if (isAbortError(reason)) return
    removeEmptyMessage(answerMessage)
    activeVoiceAnswerId = null
    voicePhase.value = 'idle'
    error.value = messageFrom(reason)
    saveConversation()
  } finally {
    if (activeQuestionRequest === controller) activeQuestionRequest = null
  }
}

async function startVoiceRecording(): Promise<void> {
  if (!voiceInputAvailable.value || sending.value || voicePhase.value !== 'idle') return
  const mimeType = RECORDING_MIME_TYPES.find(type => MediaRecorder.isTypeSupported(type))
  if (!mimeType) {
    voiceInputAvailable.value = false
    return
  }

  voicePhase.value = 'requesting'
  error.value = ''
  let stream: MediaStream
  try {
    stream = await acquireMicrophone()
  } catch {
    voicePhase.value = 'idle'
    error.value = 'Браузер не дал доступ к микрофону'
    return
  }
  if (pageUnmounted) {
    closeMicrophone()
    return
  }

  let recorder: MediaRecorder
  try {
    recorder = new MediaRecorder(stream, { mimeType })
  } catch {
    closeMicrophone()
    voicePhase.value = 'idle'
    voiceInputAvailable.value = false
    return
  }

  const chunks: Blob[] = []
  mediaRecorder = recorder
  discardRecording = false
  recorder.ondataavailable = event => {
    if (event.data.size) chunks.push(event.data)
  }
  recorder.onerror = () => {
    error.value = 'Не удалось записать звук с микрофона.'
    stopVoiceRecording(false)
  }
  recorder.onstop = () => {
    const shouldSend = !discardRecording
    const recordedType = recorder.mimeType || mimeType
    mediaRecorder = null
    clearRecordingTimer()
    recordingSeconds.value = 0
    if (!shouldSend) {
      voicePhase.value = 'idle'
      return
    }
    void sendVoiceQuestion(new Blob(chunks, { type: recordedType }))
  }

  recordingSeconds.value = 0
  const startedAt = Date.now()
  voicePhase.value = 'recording'
  try {
    recorder.start(250)
  } catch {
    mediaRecorder = null
    closeMicrophone()
    voicePhase.value = 'idle'
    voiceInputAvailable.value = false
    return
  }
  recordingTimer = window.setInterval(() => {
    const elapsedMilliseconds = Date.now() - startedAt
    const elapsed = Math.floor(elapsedMilliseconds / 1_000)
    recordingSeconds.value = Math.min(MAX_VOICE_RECORDING_SECONDS, elapsed)
    if (elapsedMilliseconds >= MAX_VOICE_RECORDING_SECONDS * 1_000 - 500) stopVoiceRecording()
  }, 250)
}

function toggleVoiceRecording(): void {
  if (voicePhase.value === 'recording') stopVoiceRecording()
  else if (voicePhase.value === 'idle') void startVoiceRecording()
}

async function playVoiceAutomatically(message: PublicConversationMessage): Promise<void> {
  await nextTick()
  const player = message.answerId
    ? document.getElementById(`answer-voice-${message.answerId}`) as HTMLAudioElement | null
    : null
  try {
    await player?.play()
  } catch {
    error.value = 'Голос готов, но браузер запретил автоматическое воспроизведение. Нажми Play.'
  } finally {
    message.voiceAutoplay = false
    saveConversation()
  }
}

function clearConversation(): void {
  if (!window.confirm('Очистить этот разговор в текущей вкладке?')) return
  activeQuestionRequest?.abort()
  activeQuestionRequest = null
  stopVoiceRecording(false)
  clearVoiceProgressTimers()
  stopStreamingVoice()
  activeVoiceAnswerId = null
  voicePhase.value = 'idle'
  messages.value = []
  for (const answerId of voicePollTimers.keys()) stopVoicePoll(answerId)
  sessionStorage.removeItem(storageKey)
  error.value = ''
}

onMounted(() => {
  pageUnmounted = false
  voiceInputAvailable.value = supportsVoiceInput()
  restoreConversation()
  void load()
})
onBeforeUnmount(() => {
  pageUnmounted = true
  activeQuestionRequest?.abort()
  activeQuestionRequest = null
  stopVoiceRecording(false)
  closeMicrophone()
  clearRecordingTimer()
  clearVoiceProgressTimers()
  stopStreamingVoice()
  for (const answerId of voicePollTimers.keys()) stopVoicePoll(answerId)
})
</script>

<template>
  <div class="public-conversation-shell">
    <audio ref="streamingVoicePlayer" hidden preload="auto" @ended="handleStreamingPartEnded" />
    <aside class="public-disclaimer">
      Ответы восстановлены по записям выступлений. Это не живой человек и не медицинская помощь.
    </aside>

    <main class="public-page public-page--conversation">
      <nav class="public-conversation-nav">
        <button class="public-back" type="button" @click="emit('navigate', '/')">Все спикеры</button>
        <button v-if="messages.length" class="public-clear" type="button" @click="clearConversation">
          Очистить разговор
        </button>
      </nav>

      <p v-if="loading" class="public-state">Открываем разговор…</p>
      <div v-else-if="!speaker" class="public-state public-state--error" role="alert">
        <p>{{ error || 'Спикер не найден.' }}</p>
        <button class="public-link-button" type="button" @click="emit('navigate', '/')">Вернуться к списку</button>
      </div>

      <template v-else>
        <header class="conversation-heading">
          <p class="public-kicker">Разговор по архиву</p>
          <h1>{{ speaker.name }}</h1>
          <p>{{ speaker.summary }}</p>
        </header>

        <section v-if="messages.length" class="conversation-messages" aria-label="Диалог">
          <article
            v-for="(message, index) in messages"
            :key="index"
            :class="['conversation-message', `conversation-message--${message.role}`]"
          >
            <p class="conversation-message__author">{{ message.role === 'user' ? 'Вы' : speaker.name }}</p>
            <div class="conversation-message__text">{{ message.text }}</div>

            <div
              v-if="message.role === 'speaker' && speaker.hasVoice && message.answerId"
              class="answer-voice"
            >
              <button
                v-if="!message.voiceStatus || message.voiceStatus === 'idle'"
                type="button"
                @click="requestVoice(message)"
              >
                Послушать голосом
              </button>
              <p v-else-if="message.voiceStatus === 'pending'">Готовим озвучку…</p>
              <audio
                v-else-if="message.voiceStatus === 'ready' && message.voiceUrl"
                controls
                preload="none"
                :src="message.voiceUrl"
                :id="`answer-voice-${message.answerId}`"
                :autoplay="message.voiceAutoplay"
                @play="handleFullVoicePlay(message.answerId)"
              >
                Браузер не поддерживает аудиоплеер.
              </audio>
              <p v-else class="answer-voice__error">Не удалось подготовить озвучку.</p>
            </div>

            <details v-if="message.role === 'speaker' && message.answerId" class="source-block">
              <summary>Откуда это</summary>
              <div class="source-group">
                <p class="source-group__title">Записи спикера</p>
                <div v-if="message.sources?.length" class="source-list">
                  <article v-for="source in message.sources" :key="source.chunkId" class="source-item">
                    <p class="source-item__meta">
                      {{ source.recordingTitle }}, {{ timecode(source.startSec) }}–{{ timecode(source.endSec) }}
                    </p>
                    <p>{{ source.text }}</p>
                    <audio controls preload="none" :src="sourceAudio(source)">
                      Браузер не поддерживает аудиоплеер.
                    </audio>
                  </article>
                </div>
                <p v-else class="source-empty">
                  Подходящих фрагментов в записях не нашлось.
                </p>
              </div>
              <div v-if="message.librarySources?.length" class="source-group source-group--library">
                <p class="source-group__title">Литература сообщества</p>
                <div class="source-list">
                  <article v-for="source in message.librarySources" :key="source.chunkId" class="source-item">
                    <p class="source-item__meta">
                      {{ source.documentTitle }}<template v-if="source.heading"> · {{ source.heading }}</template>
                    </p>
                    <p>{{ source.text }}</p>
                  </article>
                </div>
              </div>
            </details>
          </article>
        </section>

        <p v-else class="conversation-empty">
          Можно спросить о выздоровлении, опыте или теме, о которой этот человек говорил в записях.
        </p>

        <form class="question-form" @submit.prevent="ask">
          <label for="public-question">Спросите о чём хотите</label>
          <textarea
            id="public-question"
            v-model="question"
            maxlength="500"
            rows="4"
            :disabled="sending || voicePhase !== 'idle'"
            placeholder="Напишите вопрос"
          />
          <div class="question-form__footer">
            <span>{{ question.length }} из 500</span>
            <div class="question-form__actions">
              <button
                v-if="voiceInputAvailable && speaker.hasVoice"
                type="button"
                class="voice-input-button"
                :class="{ 'voice-input-button--recording': voicePhase === 'recording' }"
                :disabled="sending || (voicePhase !== 'idle' && voicePhase !== 'recording')"
                :aria-pressed="voicePhase === 'recording'"
                @click="toggleVoiceRecording"
              >
                {{ voiceButtonLabel() }}
              </button>
              <button type="submit" :disabled="sending || voicePhase !== 'idle' || !question.trim()">
                {{ sending ? 'Восстанавливаем ответ…' : 'Спросить' }}
              </button>
            </div>
          </div>
          <p v-if="error" class="public-form-error" role="alert">{{ error }}</p>
        </form>
      </template>
    </main>
  </div>
</template>
